import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  House, ArrowsLeftRight, ArrowDown, ArrowUp, Link as LinkIcon,
  Users, Code, ChartBar, SignOut, Lightning, Gear, Handshake,
  UserList, WhatsappLogo, TelegramLogo, EnvelopeSimple, Lock, List, X as CloseIcon, Money, UsersFour, Broom, GraduationCap, Megaphone, Percent,
} from "@phosphor-icons/react";
import { getUser, logout, brl } from "@/lib/api";
import { useFeatures } from "@/hooks/useFeatures";
import ChatWidget from "@/components/ChatWidget";
import MuralPopup from "@/components/MuralPopup";
import NotificationBell from "@/components/NotificationBell";

const clientNav = [
  { to: "/app", icon: House, label: "Dashboard", testid: "nav-dashboard", feature: null },
  { to: "/app/deposit", icon: ArrowDown, label: "Depositar", testid: "nav-deposit", feature: null },
  { to: "/app/transfer", icon: ArrowsLeftRight, label: "Transferir", testid: "nav-transfer", feature: null },
  { to: "/app/withdraw", icon: ArrowUp, label: "Sacar", testid: "nav-withdraw", feature: null },
  { to: "/app/funnels", icon: LinkIcon, label: "Links de Venda", testid: "nav-funnels", feature: "funnels" },
  { to: "/app/affiliate", icon: Handshake, label: "Afiliado", testid: "nav-affiliate", feature: "affiliate" },
  { to: "/app/clientes", icon: UserList, label: "Clientes (Assinaturas)", testid: "nav-clientes", feature: "clientes" },
  { to: "/app/meus-membros", icon: GraduationCap, label: "Meus Membros", testid: "nav-my-members", feature: "members" },
  { to: "/app/mural", icon: Megaphone, label: "Meu Mural", testid: "nav-mural", feature: "mural" },
  { to: "/app/whatsapp", icon: WhatsappLogo, label: "WhatsApp", testid: "nav-whatsapp", feature: "whatsapp" },
  { to: "/app/telegram", icon: TelegramLogo, label: "Telegram", testid: "nav-telegram", feature: "telegram" },
  { to: "/app/smtp", icon: EnvelopeSimple, label: "SMTP / Email", testid: "nav-smtp", feature: "smtp" },
  { to: "/app/leads", icon: Users, label: "Leads & Campanhas", testid: "nav-leads", feature: "leads" },
  { to: "/app/settings", icon: Gear, label: "Integrar Mercado Pago", testid: "nav-settings", feature: null },
];

const adminNav = [
  { to: "/app/admin", icon: ChartBar, label: "Painel Admin", testid: "nav-admin" },
  { to: "/app/admin/withdraws", icon: Money, label: "Saques", testid: "nav-admin-withdraws" },
  { to: "/app/admin/users", icon: UsersFour, label: "Usuários", testid: "nav-admin-users" },
  { to: "/app/admin/fees", icon: Percent, label: "Taxas", testid: "nav-admin-fees" },
  { to: "/app/admin/reset-balances", icon: Broom, label: "Zerar Saldos", testid: "nav-admin-reset" },
  { to: "/app/admin/mural-global", icon: Megaphone, label: "Aviso Global", testid: "nav-admin-mural" },
  { to: "/app/api-keys", icon: Code, label: "API Keys", testid: "nav-api-keys" },
];

export default function AppShell({ children }) {
  const loc = useLocation();
  const nav_ = useNavigate();
  const user = getUser();
  const isAdmin = user?.role === "admin";
  const { has, requireFeature } = useFeatures();
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen-safe md:flex" style={{ background: "var(--bg-base)" }}>
      {/* MOBILE TOP BAR */}
      <div className="md:hidden sticky top-0 z-40 border-b border-[var(--border)] bg-[var(--bg-base)]/95 backdrop-blur-md px-4 py-3 flex items-center justify-between">
        <Link to="/app" className="flex items-center gap-2">
          <Lightning size={22} weight="fill" color="var(--accent)" />
          <span className="font-display font-black text-base tracking-tight">MERCADOPAGU</span>
        </Link>
        <button onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle"
          className="w-10 h-10 rounded-xl border border-white/10 bg-white/[0.02] flex items-center justify-center hover:border-[var(--accent)] transition-colors">
          {mobileOpen ? <CloseIcon size={16} /> : <List size={16} />}
        </button>
      </div>

      {/* Backdrop mobile */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-30 bg-black/70 backdrop-blur-md" onClick={closeMobile} />
      )}

      {/* SIDEBAR */}
      <aside
        className={`${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"} fixed md:sticky inset-y-0 left-0 z-40 md:z-auto w-72 shrink-0 border-r border-white/[0.06] flex flex-col md:top-0 h-screen transition-transform duration-300 overflow-hidden`}
        style={{
          background:
            "linear-gradient(180deg, rgba(10,13,18,1) 0%, rgba(5,7,9,1) 100%)",
        }}
        data-testid="app-sidebar"
      >
        {/* orbe decorativo */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -left-24 w-64 h-64 rounded-full opacity-40"
          style={{
            background: "radial-gradient(circle, rgba(255,179,15,0.25) 0%, transparent 60%)",
            filter: "blur(30px)",
          }}
        />

        <div className="relative flex-1 overflow-y-auto p-5 md:p-6 flex flex-col gap-1">
          <Link to="/app" onClick={closeMobile} className="hidden md:flex items-center gap-2.5 mb-8 group" data-testid="brand-link">
            <div className="w-9 h-9 rounded-lg bg-[var(--accent)]/10 border border-[var(--accent)]/40 flex items-center justify-center group-hover:bg-[var(--accent)]/20 transition-colors">
              <Lightning size={20} weight="fill" color="var(--accent)" />
            </div>
            <div>
              <div className="font-display font-black text-lg tracking-tight leading-none group-hover:text-[var(--accent)] transition-colors">MERCADOPAGU</div>
              <div className="label-caps text-[9px] leading-none mt-1.5 tracking-widest">Digital Bank · 2026</div>
            </div>
          </Link>
          <div className="label-caps mb-2 text-[10px]">Menu</div>
          {clientNav.map((n) => {
            const active = loc.pathname === n.to;
            const Icon = n.icon;
            const locked = !isAdmin && n.feature && !has(n.feature);
            if (locked) {
              return (
                <button
                  key={n.to}
                  type="button"
                  onClick={() => { closeMobile(); requireFeature(n.feature); }}
                  data-testid={n.testid}
                  className="nav-pill nav-pill-locked group"
                  title="Clique para ver planos que liberam este recurso"
                >
                  <Icon size={17} weight="regular" />
                  <span className="flex-1 truncate">{n.label}</span>
                  <Lock size={11} weight="fill" color="var(--accent)" />
                </button>
              );
            }
            return (
              <Link
                key={n.to}
                to={n.to}
                onClick={closeMobile}
                data-testid={n.testid}
                className={`nav-pill ${active ? "nav-pill-active" : ""}`}
              >
                <Icon size={17} weight={active ? "fill" : "regular"} />
                <span className="flex-1 truncate">{n.label}</span>
                {active && <span className="w-1.5 h-1.5 rounded-full bg-black/40" />}
              </Link>
            );
          })}
          {isAdmin && (
            <>
              <div className="label-caps mb-2 mt-6 text-[10px]">Admin</div>
              {adminNav.map((n) => {
                const active = loc.pathname === n.to;
                const Icon = n.icon;
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    onClick={closeMobile}
                    data-testid={n.testid}
                    className={`nav-pill ${active ? "nav-pill-active" : ""}`}
                  >
                    <Icon size={17} weight={active ? "fill" : "regular"} />
                    <span className="flex-1 truncate">{n.label}</span>
                    {active && <span className="w-1.5 h-1.5 rounded-full bg-black/40" />}
                  </Link>
                );
              })}
            </>
          )}
        </div>

        {/* Footer conta */}
        <div className="relative border-t border-white/[0.06] p-5 md:p-6 bg-black/20">
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 backdrop-blur-sm" data-testid="sidebar-user-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[var(--accent)] to-[#c48808] flex items-center justify-center text-black font-black text-sm shrink-0">
                {(user?.name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate text-white" data-testid="sidebar-user-name">{user?.name}</div>
                <div className="text-[10px] text-white/40 truncate font-mono">{user?.email}</div>
              </div>
            </div>
            <div className="label-caps text-[9px] mb-1">{isAdmin ? "Comissões" : "Saldo disponível"}</div>
            <div className="font-display font-black text-2xl text-[var(--accent)] tracking-tighter tabular-nums" data-testid="sidebar-balance">
              {brl(user?.balance || 0)}
            </div>
            <button
              onClick={() => { logout(); }}
              data-testid="logout-btn"
              className="mt-3 w-full flex items-center justify-center gap-2 text-xs text-white/50 hover:text-[var(--danger)] transition-colors py-2 rounded-lg border border-white/[0.06] hover:border-[var(--danger)]/40 hover:bg-[var(--danger)]/5"
            >
              <SignOut size={13} /> Sair
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-4 md:p-8 lg:p-12 max-w-[1400px] w-full">{children}</main>
      <ChatWidget />
      <MuralPopup audience="client" />
      <NotificationBell />
    </div>
  );
}
